import { ChevronLeft } from "@mui/icons-material";
import {
  Box,
  Divider,
  List, ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText, Drawer as MuiDrawer,
  SwipeableDrawer, useMediaQuery, useTheme
} from "@mui/material";
import IconButton from "../ui/IconButton";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { navigationOptions as allOptions } from "../../constants/navigation";
import { RoleGuard } from "../ui/RoleGuard";
import { useIntl } from "../../contexts/IntlContext";

interface DrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function Drawer({ open, setOpen }: DrawerProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (!isMobile) return;
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;
    if (open) setOpen(false);
  }, [isMobile, location.pathname, open, setOpen]);

  const commonDrawerSx = {
    width: isMobile ? 280 : open ? 240 : 64,
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: isMobile ? 280 : open ? 240 : 64,
      transition: theme.transitions.create("width"),
      overflowX: "hidden",
      boxSizing: "border-box" as const,
    },
  };

  const content = (
    <>
      <Box
        sx={{
          ...theme.mixins.toolbar,
          display: "flex",
          justifyContent: "flex-end",
          px: 1,
          height: 64,
        }}
      >
        <IconButton onClick={() => setOpen(false)}>
          <ChevronLeft />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {allOptions.map((option) => (
          <RoleGuard key={option.nameKey} allowedRoles={option.roles}>
            <ListItem
              disablePadding
              onClick={() => {
                navigate(option.route);
                setOpen(false);
              }}
            >
              <ListItemButton
                selected={
                  location.pathname === option.route ||
                  (option.route !== "/" && location.pathname.startsWith(option.route))
                }
                sx={{
                  minHeight: isMobile ? 52 : 48,
                  px: 2.5,
                  justifyContent: isMobile ? "initial" : open ? "initial" : "center",
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isMobile ? 2 : open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {option.icon}
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({ id: 'drawer.' + option.nameKey })}
                  sx={{
                    opacity: isMobile ? 1 : open ? 1 : 0,
                    whiteSpace: "nowrap",
                    ...(!isMobile && !open && { width: 0, overflow: "hidden" }),
                  }}
                />
              </ListItemButton>
            </ListItem>
          </RoleGuard>
        ))}
      </List>
    </>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        variant="temporary"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableBackdropTransition={!isIOS}
        ModalProps={{ keepMounted: true }}
        slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.75)" } } }}
        sx={commonDrawerSx}
      >
        {content}
      </SwipeableDrawer>
    );
  }

  return (
    <MuiDrawer variant="permanent" open={open} onClose={() => setOpen(false)} sx={commonDrawerSx}>
      {content}
    </MuiDrawer>
  );
}
